<a
  [routerLink]="['/properties', property()._id]"
  class="group relative bg-white dark:bg-neutral-2 border border-neutral-3 overflow-hidden hover:shadow-2xl hover:scale-[1.01] hover:border-indigo-6/60 transition-all duration-300 flex flex-col cursor-pointer no-underline text-inherit"
  [class.h-full]="!compact()"
  [class.rounded-2xl]="compact()"
  [class.rounded-3xl]="!compact()"
>
  <!-- Header Photo & Banner -->
  <div
    class="w-full bg-neutral-3 relative overflow-hidden flex flex-col justify-between"
    [class.h-36]="compact()"
    [class.p-3]="compact()"
    [class.h-52]="!compact()"
    [class.p-4]="!compact()"
    [etxePropertyHoverPreview]="property()"
  >
    @if (coverPhotoUrl(); as coverUrl) {
      <img
        [src]="coverUrl"
        [alt]="property().presentation?.title || 'Photo du bien'"
        class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/55"></div>
    } @else {
      <div class="absolute inset-0 bg-gradient-to-br from-indigo-9 via-violet-9 to-purple-9"></div>
    }

    <!-- Top Badges -->
    <div class="flex justify-between items-start relative z-10 gap-2">
      <div class="flex flex-wrap gap-1.5 items-center">
        <span
          class="rounded-full bg-white/20 backdrop-blur-md text-white shadow-sm border border-white/10 font-bold uppercase tracking-wider"
          [class.px-2.5]="compact()"
          [class.py-0.5]="compact()"
          [class.text-[10px]]="compact()"
          [class.px-3]="!compact()"
          [class.py-1]="!compact()"
          [class.text-xs]="!compact()"
        >
          {{ propertyTypeLabel() }}
        </span>
        @if (!compact() && property().mandate?.type; as mandateType) {
          <span
            class="rounded-full bg-indigo-950/70 backdrop-blur-md px-2.5 py-1 text-xs font-semibold text-indigo-2 border border-indigo-4/30 shadow-sm"
          >
            Mandat {{ mandateTypeLabel() }}
          </span>
        }
      </div>

      <div class="flex items-center gap-1.5">
        @if (isApplied()) {
          <span
            class="rounded-full bg-emerald-600/90 backdrop-blur-md text-white flex items-center gap-1 shadow-sm border border-emerald-400/20 font-bold"
            [class.px-2]="compact()"
            [class.py-0.5]="compact()"
            [class.text-[10px]]="compact()"
            [class.px-3]="!compact()"
            [class.py-1]="!compact()"
            [class.text-xs]="!compact()"
          >
            <mat-icon class="size-3! !text-xs">done</mat-icon>
            Candidaté
          </span>
        }
      </div>
    </div>

    <!-- Bottom Location & Photo counter -->
    <div class="flex justify-between items-end relative z-10 text-white gap-2">
      <div class="flex flex-col min-w-0">
        <div
          class="flex items-center gap-1 text-xs font-bold drop-shadow-sm truncate"
          [class.sm:text-sm]="!compact()"
        >
          <mat-icon class="size-3.5! !text-sm text-indigo-3 shrink-0">location_on</mat-icon>
          <span class="truncate">{{ property().location?.address?.city || 'France' }}</span>
          @if (property().location?.address?.zipCode; as zip) {
            <span class="text-[11px] font-medium text-white/80 shrink-0">({{ zip }})</span>
          }
        </div>
        @if (!compact() && property().location?.address?.region; as region) {
          <span class="text-[11px] text-white/70 ml-5 truncate">{{ region }}</span>
        }
      </div>

      @if (property().media?.photos?.length; as photosCount) {
        <div
          class="flex items-center gap-1 bg-black/60 backdrop-blur-md px-2 py-0.5 rounded-full text-[11px] font-medium text-white/90 border border-white/10 shadow-sm shrink-0"
        >
          <mat-icon class="size-3! !text-xs">photo_camera</mat-icon>
          <span>{{ photosCount }}</span>
        </div>
      }
    </div>
  </div>

  <!-- Body Content -->
  <div
    class="flex flex-col"
    [class.flex-1]="!compact()"
    [class.p-3.5]="compact()"
    [class.gap-2]="compact()"
    [class.p-5]="!compact()"
    [class.gap-3.5]="!compact()"
  >
    <div>
      <h3
        class="font-bold text-neutral-12 line-clamp-1 group-hover:text-indigo-11 transition-colors"
        [class.text-sm]="compact()"
        [class.text-base]="!compact()"
        [title]="property().presentation?.title || 'Bien sans titre'"
      >
        {{ property().presentation?.title || 'Bien sans titre' }}
      </h3>
      <!-- Key specs row -->
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-11 mt-1 font-medium">
        <span class="inline-flex items-center gap-1 text-neutral-12 font-semibold">
          <mat-icon class="text-xs! w-3.5! h-3.5! text-neutral-9">straighten</mat-icon>
          {{ property().keyInfo?.surface?.habitable || 0 }} m²
        </span>
        <span>•</span>
        <span>{{ roomsCount() }} pièce{{ roomsCount() > 1 ? 's' : '' }}</span>
        @if (bedroomsCount(); as beds) {
          <span>•</span>
          <span>{{ beds }} ch.</span>
        }
        @if (!compact() && property().keyInfo?.surface?.land; as land) {
          <span>•</span>
          <span class="text-neutral-10">Terrain {{ land }} m²</span>
        }
      </div>
    </div>

    <!-- Financial & Mandate Details -->
    <div
      class="rounded-2xl bg-neutral-2 border border-neutral-3"
      [class.flex]="compact()"
      [class.flex-col]="compact()"
      [class.divide-y]="compact()"
      [class.divide-neutral-4/60]="compact()"
      [class.p-2.5]="compact()"
      [class.grid]="!compact()"
      [class.grid-cols-3]="!compact()"
      [class.gap-2.5]="!compact()"
      [class.py-2.5]="!compact()"
      [class.px-3]="!compact()"
    >
      <div
        [class.flex]="compact()"
        [class.justify-between]="compact()"
        [class.items-center]="compact()"
        [class.pb-1.5]="compact()"
      >
        <span class="text-[10px] text-neutral-10 block uppercase font-bold tracking-wider">Prix</span>
        <span
          class="font-extrabold text-neutral-12 truncate block"
          [class.text-xs]="compact()"
          [class.text-sm]="!compact()"
        >
          @if (property().financials?.sellingPriceCents; as priceCents) {
            {{ priceCents / 100 | number: '1.0-0' : 'fr-FR' }} €
          } @else {
            Non spécifié
          }
        </span>
      </div>

      <div
        [class.flex]="compact()"
        [class.justify-between]="compact()"
        [class.items-center]="compact()"
        [class.py-1.5]="compact()"
        [class.border-x]="!compact()"
        [class.border-neutral-4/60]="!compact()"
        [class.px-2.5]="!compact()"
      >
        <div class="flex items-center gap-1">
          <span class="text-[10px] text-neutral-10 block uppercase font-bold tracking-wider">Commission</span>
          <mat-icon
            class="text-xs! w-3! h-3! text-neutral-8 hover:text-indigo-11 cursor-help"
            matTooltip="Taux recherché par le vendeur. Vous pouvez candidater avec un taux différent ou supérieur."
            matTooltipPosition="above"
          >
            info
          </mat-icon>
        </div>
        <span
          class="font-extrabold text-indigo-11 truncate block"
          [class.text-xs]="compact()"
          [class.text-sm]="!compact()"
        >
          @if (property().mandate?.targetCommissionRate; as rate) {
            {{ rate }} %
          } @else {
            Libre
          }
        </span>
      </div>

      <div
        [class.flex]="compact()"
        [class.justify-between]="compact()"
        [class.items-center]="compact()"
        [class.pt-1.5]="compact()"
      >
        <span class="text-[10px] text-neutral-10 block uppercase font-bold tracking-wider">Durée</span>
        <span
          class="font-bold text-neutral-11 truncate block"
          [class.text-xs]="true"
          [class.mt-0.5]="!compact()"
        >
          @if (property().mandate?.duration) {
            {{ mandateDurationLabel() }}
          } @else {
            -
          }
        </span>
      </div>
    </div>

    <!-- Application Progress / Scarcity -->
    @let count = candidatesCount();
    @let max = maxCandidates();
    @let accepting = isAcceptingCandidates();
    <div
      class="flex flex-col gap-1.5 pt-2 border-t border-neutral-3/60"
      [class.mt-auto]="!compact()"
      [class.mt-1]="compact()"
    >
      <div class="flex justify-between items-center text-xs font-semibold">
        <span class="text-neutral-10">Agents retenus</span>
        <div class="flex items-center gap-1.5">
          @if (appliedCount(); as pending) {
            <span class="text-[10px] text-neutral-10 font-normal">({{ pending }} en attente)</span>
          }
          <span [class]="count >= max || !accepting ? 'text-red-9' : 'text-neutral-12'">
            {{ count }} / {{ max }}
          </span>
        </div>
      </div>
      <!-- Progress Bar -->
      <div class="h-2 w-full bg-neutral-3 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-500"
          [class.bg-indigo-9]="count < 3 && accepting"
          [class.bg-orange-9]="count >= 3 && count < max && accepting"
          [class.bg-red-9]="count >= max || !accepting"
          [style.width.%]="(count / max) * 100"
        ></div>
      </div>
    </div>
  </div>
</a>
